import { useMemo, useState } from "react";
import { format } from "date-fns";

import { Namespace, useT } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { ProgressBar } from "~/ui/components/progressBar";
import { AnalyticsDataSection } from "~/ui/components/analyticsDataSection";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { DateView, getLastSevenDaysDates } from "./dateView";
import { StatCard } from "./statCard";
import {
  useDisposalSiteByIdQuery,
  useGetDisposalSiteAnalyticsQuery,
  useGetGovRawScaleRecordByCompanyQuery,
  useGetGovRawScaleRecordListQuery,
} from "~/shared/api/govDash/govDash.api";
import { toLocalWallClockTimestamp } from "~/shared/utils/filterDateFormatter";
import {
  RECYCLING_POINT_BADGE_COLORS,
  toPercentBadge,
  ReportBadgeColor,
} from "~/shared/utils/badgeFormatters";

const formatTo1 = (value: number) => value.toFixed(1);

export const DisposalSiteDetails = ({
  selectedRecordId,
}: {
  selectedRecordId: string | undefined;
}) => {
  const t = useT();
  const [activeSection, setActiveSection] = useState<"general" | "summary" | "weightManagement">(
    "general",
  );
  const { data: disposalSiteData } = useDisposalSiteByIdQuery({
    id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
  });
  const [{ weekStart, weekEnd }, setDateRange] = useState(() => getLastSevenDaysDates());

  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
        {
          value: "weightManagement" as const,
          label: t(Namespace.Govdash, "weightManagement"),
        },
      ] as const,
    [t],
  );

  const generalRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "juridicallName"),
        value: disposalSiteData?.name || "-",
      },
      {
        label: t(Namespace.Govdash, "bin"),
        value: disposalSiteData?.bin || "-",
      },
      {
        label: t(Namespace.Govdash, "jurAddress"),
        value: disposalSiteData?.jurAddress || "-",
      },
      // INFO: Kuna said, we do not have these for disposable sites
      // {
      //   label: t(Namespace.Govdash, "geolocation"),
      //   value: "2gis link",
      // },
      // { label: t(Namespace.Govdash, "area"), value: "676 ha" },
    ],
    [t, disposalSiteData],
  );

  return (
    <CardContentWrapper
      hasDivider={activeSection !== "general"}
      className="flex min-h-0 flex-col overflow-hidden pb-2"
    >
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      <div className="overflow-y-auto min-h-0 flex-1">
        {activeSection === "general" && (
          <>
            <div className="space-y-4 p-4">
              <CardContentWrapper>
                {generalRows.map((row) => (
                  <InfoRow key={row.label} label={row.label} value={row.value} />
                ))}
              </CardContentWrapper>
            </div>
          </>
        )}
        {activeSection === "summary" && (
          <>
            <div className="space-y-4 p-4">
              <DateView
                weekStart={weekStart}
                weekEnd={weekEnd}
                onDateChange={(from, to) => setDateRange({ weekStart: from, weekEnd: to })}
                containerClassName="!p-0 !px-2"
              />
            </div>
            <DisposalSiteSummary
              selectedRecordId={selectedRecordId}
              weekStart={weekStart}
              weekEnd={weekEnd}
            />
          </>
        )}
        {activeSection === "weightManagement" && (
          <>
            <DateView
              weekStart={weekStart}
              weekEnd={weekEnd}
              onDateChange={(from, to) => setDateRange({ weekStart: from, weekEnd: to })}
            />
            <WeightManagementView
              selectedRecordId={selectedRecordId}
              weekStart={weekStart}
              weekEnd={weekEnd}
            />
          </>
        )}
      </div>
    </CardContentWrapper>
  );
};

const DisposalSiteSummary = ({
  selectedRecordId,
  weekStart,
  weekEnd,
}: {
  selectedRecordId: string | undefined;
  weekStart: Date;
  weekEnd: Date;
}) => {
  const t = useT();
  const { data: companyScaleList } = useGetGovRawScaleRecordByCompanyQuery(
    {
      ownerOrganizationId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );

  const { data: disposalSiteAnalytics } = useGetDisposalSiteAnalyticsQuery(
    {
      ownerOrganizationId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );

  const recyclingItems = useMemo(() => {
    const records = companyScaleList?.records ?? [];
    const entries = records
      .map((record: any) => ({
        organizationName: record?.organizationName ?? "",
        ownerOrganizationId: String(record?.ownerOrganizationId ?? ""),
        totalNettoKg: Number(record?.totalNettoKg ?? 0),
      }))
      .filter((entry) => entry.organizationName && entry.totalNettoKg > 0 && Number(entry.ownerOrganizationId) != 43)
      .sort((a, b) => b.totalNettoKg - a.totalNettoKg);

    const totalNettoKgAll = entries.reduce((sum, entry) => sum + entry.totalNettoKg, 0);

    return entries.map((entry, index) => {
      const badgeColor =
        entry.organizationName.trim() === "Другое"
          ? "bg-gray-800"
          : (RECYCLING_POINT_BADGE_COLORS[
              index % RECYCLING_POINT_BADGE_COLORS.length
            ] as ReportBadgeColor);

      return {
        badge: toPercentBadge(entry.totalNettoKg, totalNettoKgAll),
        badgeColor,
        value: entry.totalNettoKg,
        subtitle: `${formatTo1(entry.totalNettoKg / 1000)} т`,
        label: entry.organizationName,
        showInProgressBar: true,
      };
    });
  }, [companyScaleList?.records]);

  const progressItems = useMemo(
    () =>
      recyclingItems.map((item) => ({
        color: item.badgeColor,
        value: Number(item.value) || 0,
      })),
    [recyclingItems],
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-2 p-4">
        <StatCard
          title={t(Namespace.Govdash, "actualWaste")}
          value={disposalSiteAnalytics?.totalRecords.toString() ?? 0}
        />
        <StatCard
          title={t(Namespace.Govdash, "wasteTonnage")}
          value={
            disposalSiteAnalytics?.totalNettoKg
              ? formatTo1(Number(disposalSiteAnalytics.totalNettoKg) / 1000)
              : 0
          }
        />
        {/*{disposalSiteAnalytics?.weightsByWasteType?.WASTE_TYPE_MSW !== undefined && (
          <StatCard
            title={t(Namespace.Govdash, "mswTonnage")}
            value={formatTo1(disposalSiteAnalytics.weightsByWasteType.WASTE_TYPE_MSW / 1000)}
          />
        )}
        {disposalSiteAnalytics?.weightsByWasteType?.WASTE_TYPE_CONSTRUCTION !== undefined && (
          <StatCard
            title={t(Namespace.Govdash, "cwTonnage")}
            value={formatTo1(
              disposalSiteAnalytics.weightsByWasteType.WASTE_TYPE_CONSTRUCTION / 1000,
            )}
          />
        )}*/}
      </div>
      <div className="space-y-3 p-4">
        <div className="w-full flex items-center justify-between px-1 -mx-1">
          <h3 className="text-base font-semibold text-gray-900 leading-tight">
            {t(Namespace.Govdash, "mvoShare")}
          </h3>
        </div>

        <ProgressBar
          items={progressItems}
          valueType="percentage"
          minSegmentPct={10}
          minSegmentWidthPx={35}
        />

        <div className="grid grid-cols-2 gap-2">
          {recyclingItems.map((item) => (
            <AnalyticsDataSection
              key={`${item.label}-${item.badge}`}
              badge={item.badge}
              badgeColor={item.badgeColor}
              subtitle={item.subtitle}
              label={item.label}
              gapClassName="gap-2"
            />
          ))}
        </div>
      </div>
    </>
  );
};

const WeightManagementView = ({
  selectedRecordId,
  weekStart,
  weekEnd,
}: {
  selectedRecordId: string | undefined;
  weekStart: Date;
  weekEnd: Date;
}) => {
  const t = useT();
  const { data: { rawScaleRecords: rawScaleRecordList } = {} } = useGetGovRawScaleRecordListQuery(
    {
      ownerOrganizationId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );

  return (
    <>
      {rawScaleRecordList && rawScaleRecordList.length === 0 ? (
        <div className="p-4 text-center text-sm text-slate-500">
          {t(Namespace.Common, "noResults")}
        </div>
      ) : (
        <>
          {rawScaleRecordList?.map((weight) => (
            <div
              key={weight.id}
              className="flex w-full items-center gap-4 border-slate-200 bg-white p-4 text-left"
            >
              <span className="text-xs/4 font-bold uppercase leading-none tracking-tight text-slate-900">
                {weight.id.toString()}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="inline-flex rounded border border-slate-900 px-1 py-0.5 text-xs font-medium leading-4 tracking-tight text-slate-900">
                      {weight.truckNumber}
                    </span>
                  </div>
                  <span className="flex flex-wrap items-center justify-between gap-2 text-xs font-normal leading-4 tracking-tight">
                    {weight.sender}
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-normal leading-4 tracking-tight text-text-secondary">
                  <span>
                    {weight.firstTimeScaledAt
                      ? format(
                          new Date(Number(weight.firstTimeScaledAt.seconds) * 1000),
                          "HH:mm dd.MM.yyyy",
                        )
                      : "—"}
                  </span>
                  <span>
                    {weight.wasteType} {formatTo1(weight.nettoKg / 1000)}{" "}
                    {t(Namespace.Govdash, "tonnage")}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
};
